import { motion } from 'motion/react';
import { useEffect, useState } from 'react';

interface MetricProps {
  value: string;
  label: string;
  delay?: number;
}

function AnimatedMetric({ value, label, delay = 0 }: MetricProps) {
  const [count, setCount] = useState(0);

  // Check if the value is a pure number (with optional %,+, etc.)
  const isNumeric = /^[\d,.]+%?\+?$/.test(value);

  useEffect(() => {
    if (!isNumeric) return; // Skip animation for non-numeric values

    const numericValue = parseFloat(value.replace(/[^\d.]/g, ''));
    const timer = setTimeout(() => {
      let start = 0;
      const end = numericValue;
      const duration = 2000;
      const increment = end / (duration / 16);

      const counter = setInterval(() => {
        start += increment;
        if (start >= end) {
          setCount(end);
          clearInterval(counter);
        } else {
          setCount(start);
        }
      }, 16);

      return () => clearInterval(counter);
    }, delay * 1000);

    return () => clearTimeout(timer);
  }, [value, delay, isNumeric]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay }}
      viewport={{ once: true }}
      className="text-center flex flex-col items-center justify-center h-full min-h-[120px]"
    >
      <motion.div
        className="text-4xl lg:text-5xl font-bold neon-text mb-3"
        whileHover={{ scale: 1.05 }}
        transition={{ duration: 0.3 }}
      >
        {isNumeric
          ? `${count.toFixed(value.includes('.') ? 1 : 0)}${value.replace(/[\d.]/g, '')}`
          : value}
      </motion.div>
      <p className="text-muted-foreground text-lg font-medium text-center leading-tight">{label}</p>
    </motion.div>
  );
}


export function TrustMetrics() {
  return (
    <section className=" bg-gradient-to-r">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">Trusted by Industry Leaders</h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto text-center">
            Our AI solutions deliver measurable results across all implementations
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-center justify-center">
          <AnimatedMetric
            value="15+"
            label="Verticles"
            delay={0.2}
          />
          <AnimatedMetric
            value="20+"
            label="Projects"
            delay={0.4}
          />
          <AnimatedMetric
            value="24/7"
            label="AI Operations"
            delay={0.6}
          />
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-primary/5 to-accent/5 rounded-full blur-3xl" />
        </div>
      </div>
    </section>
  );
}